import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import { useFilter } from '../contexts/FilterContext';
import { getRefreshPaperStyles, getRefreshTableStyles } from '../utils/refreshAnimation';
import { bankaService } from '../services/bankaService';
import type { Banka } from '../types/banka';
import { formatDateToTR } from '../utils/dateUtils';

const Bankalar = () => {
  const { refreshTrigger, isRefreshing } = useFilter();
  const navigate = useNavigate();
  const [bankalar, setBankalar] = useState<Banka[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBanka, setSelectedBanka] = useState<Banka | null>(null);

  const loadBankalar = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bankaService.getAll();
      setBankalar(data);
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : null;
      setError(message || "Bankalar yüklenirken bir hata oluştu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBankalar();
  }, [loadBankalar, refreshTrigger]);

  const handleCreate = () => {
    navigate('/estatement/bankalar/new');
  };

  const handleEdit = (id: string) => {
    navigate(`/estatement/bankalar/${id}/edit`);
  };

  const handleDeleteClick = (banka: Banka) => {
    setSelectedBanka(banka);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBanka) return;
    try {
      await bankaService.delete(selectedBanka.id);
      await loadBankalar();
      setDeleteDialogOpen(false);
      setSelectedBanka(null);
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : null;
      alert(message || 'Banka silinirken bir hata oluştu');
    }
  };

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          opacity: isRefreshing ? 0.7 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isRefreshing ? 'none' : 'auto',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Bankalar
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Yeni Banka
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="280px">
            <CircularProgress />
          </Box>
        ) : bankalar.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', ...getRefreshPaperStyles(isRefreshing) }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Henüz banka bulunmuyor.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ mt: 2 }}
            >
              İlk Bankayı Ekle
            </Button>
          </Paper>
        ) : (
          <Paper
            sx={{
              borderRadius: 3,
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)',
              overflow: 'hidden',
              ...getRefreshPaperStyles(isRefreshing),
            }}
          >
            <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Table size="small" sx={getRefreshTableStyles(isRefreshing)}>
                <TableHead>
                  <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Banka Kodu</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Banka Adı</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Durum</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Oluşturulma Tarihi</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }} align="right">İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bankalar.map((b, index) => (
                    <TableRow
                      key={b.id}
                      hover
                      sx={{
                        bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
                        '&:hover': { bgcolor: 'action.selected' },
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{b.bankaKodu}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{b.bankaAdi}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Chip label={b.isActive ? 'Aktif' : 'Pasif'} size="small" sx={{ height: 22, fontSize: '0.75rem' }} color={b.isActive ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{formatDateToTR(b.createdAt)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }} align="right">
                        <IconButton size="small" onClick={() => handleEdit(b.id)} color="primary" aria-label="Düzenle">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteClick(b)} color="error" aria-label="Sil">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Banka Sil</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {selectedBanka?.bankaAdi} ({selectedBanka?.bankaKodu}) silinecek. Bu işlem geri alınamaz.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Sil
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default Bankalar;
